import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Form, Input, Modal, Popconfirm, Select, Switch, Table, Tooltip, Typography, message,
} from 'antd';
import { PlusOutlined, KeyOutlined, CopyOutlined, StopOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { staffApi, StaffMember, StaffRole, ROLE_OPTIONS } from '../../api/staff';
import { PageHeader, StatusDot } from '../../components/shared';
import { getUser } from '../../utils/auth';
import { L } from '../../styles/theme';

const { Text } = Typography;

const ROLE_COLOR: Record<string, string> = {
  admin: L.accent,
  moderator: L.violet,
  operator: L.teal,
};

const StaffList: React.FC = () => {
  const [items, setItems] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [linkModal, setLinkModal] = useState<{ link: string; mailSent: boolean; email: string } | null>(null);
  const me = getUser();

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await staffApi.list();
      setItems(data);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось загрузить сотрудников');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleCreate = async () => {
    let values: any;
    try { values = await form.validateFields(); } catch { return; }
    setSubmitting(true);
    try {
      const { data } = await staffApi.create(values);
      setCreateOpen(false);
      form.resetFields();
      fetch();
      setLinkModal({
        link: data.invite_link || '',
        mailSent: !!data.mail_sent,
        email: data.email || values.email,
      });
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось создать сотрудника');
    } finally {
      setSubmitting(false);
    }
  };

  const changeRole = async (m: StaffMember, role: StaffRole) => {
    try {
      await staffApi.update(m.id, { role });
      message.success('Роль изменена');
      fetch();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось изменить роль');
      fetch();
    }
  };

  const toggleActive = async (m: StaffMember, is_active: boolean) => {
    try {
      await staffApi.update(m.id, { is_active });
      message.success(is_active ? 'Доступ включён' : 'Доступ приостановлен');
      fetch();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось изменить');
      fetch();
    }
  };

  const sendReset = async (m: StaffMember) => {
    try {
      const { data } = await staffApi.sendReset(m.id);
      setLinkModal({ link: data.invite_link, mailSent: data.mail_sent, email: m.email || '' });
      fetch();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось выслать ссылку');
    }
  };

  const revoke = async (m: StaffMember) => {
    try {
      await staffApi.revoke(m.id);
      message.success('Доступ отозван');
      fetch();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось отозвать доступ');
    }
  };

  const columns: ColumnsType<StaffMember> = [
    {
      title: 'Сотрудник',
      key: 'name',
      render: (_, r) => (
        <div>
          <div style={{ fontSize: 12.5, color: L.text }}>
            {r.full_name || '—'}
            {me?.id === r.id && (
              <span style={{ fontSize: 10.5, color: L.faint, marginLeft: 6 }}>это вы</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: L.faint }}>{r.email}</div>
        </div>
      ),
    },
    {
      title: 'Роль',
      key: 'role',
      width: 170,
      render: (_, r) => (
        <Select
          size="small"
          value={r.role as StaffRole}
          style={{ width: 150 }}
          disabled={me?.id === r.id}
          onChange={(v) => changeRole(r, v)}
          options={ROLE_OPTIONS.map((o) => ({
            value: o.value,
            label: (
              <span>
                <span className="status-dot" style={{ background: ROLE_COLOR[o.value] }} />
                {o.label}
              </span>
            ),
          }))}
        />
      ),
    },
    {
      title: 'Пароль',
      key: 'has_password',
      width: 150,
      render: (_, r) =>
        r.has_password ? (
          <StatusDot color={L.ok} label="задан" />
        ) : (
          <StatusDot color={L.warn} label="ждёт приглашения" />
        ),
    },
    {
      title: 'Последний вход',
      key: 'last_login_at',
      width: 140,
      render: (_, r) =>
        r.last_login_at ? (
          <span className="num" style={{ fontSize: 11.5 }}>
            {dayjs(r.last_login_at).format('DD.MM.YYYY HH:mm')}
          </span>
        ) : (
          <Text type="secondary" style={{ fontSize: 11.5 }}>ни разу</Text>
        ),
    },
    {
      title: 'Доступ',
      key: 'is_active',
      width: 90,
      render: (_, r) => (
        <Switch
          size="small"
          checked={r.is_active}
          disabled={me?.id === r.id}
          onChange={(v) => toggleActive(r, v)}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, r) => (
        <span style={{ display: 'flex', gap: 4 }}>
          <Tooltip title={r.has_password ? 'Выслать ссылку на смену пароля' : 'Выслать приглашение заново'}>
            <Button size="small" icon={<KeyOutlined />} onClick={() => sendReset(r)} />
          </Tooltip>
          {me?.id !== r.id && (
            <Popconfirm
              title="Отозвать доступ?"
              description="Человек останется в базе как житель, но в панель войти не сможет."
              okText="Отозвать"
              okButtonProps={{ danger: true }}
              cancelText="Отмена"
              onConfirm={() => revoke(r)}
            >
              <Tooltip title="Отозвать доступ">
                <Button size="small" danger icon={<StopOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Сотрудники"
        subtitle={`${items.length} с доступом в панель`}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Добавить сотрудника
          </Button>
        }
      />

      <div style={{
        border: `1px solid ${L.line}`, borderRadius: 10, background: L.surface,
        boxShadow: L.shadow, overflow: 'hidden',
      }}>
        <Table<StaffMember>
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'Сотрудников пока нет' }}
        />
      </div>

      <div style={{ fontSize: 11.5, color: L.faint, marginTop: 10, lineHeight: 1.6 }}>
        Пароль задаёт сам сотрудник по ссылке из письма — администратор его не видит и не назначает.
      </div>

      <Modal
        title="Новый сотрудник"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        confirmLoading={submitting}
        okText="Создать и выслать приглашение"
        cancelText="Отмена"
        destroyOnClose
        width={460}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 14 }} initialValues={{ role: 'operator' }}>
          <Form.Item
            name="email"
            label="Рабочая почта (это и есть логин)"
            rules={[
              { required: true, message: 'Введите почту' },
              { type: 'email', message: 'Похоже, адрес с опечаткой' },
            ]}
          >
            <Input placeholder="name@aqsaiym.app" autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="full_name"
            label="Имя и фамилия"
            rules={[{ required: true, message: 'Введите имя' }]}
          >
            <Input placeholder="Айгүл Сериковна" />
          </Form.Item>
          <Form.Item name="role" label="Роль" rules={[{ required: true }]}>
            <Select
              options={ROLE_OPTIONS.map((o) => ({
                value: o.value,
                label: (
                  <span>
                    {o.label}
                    <span style={{ color: L.faint, fontSize: 11, marginLeft: 6 }}>{o.hint}</span>
                  </span>
                ),
              }))}
            />
          </Form.Item>
          <Form.Item name="phone" label="Телефон (необязательно)">
            <Input placeholder="+7 705 000 00 00" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={linkModal?.mailSent ? 'Приглашение отправлено' : 'Передайте ссылку сотруднику'}
        open={!!linkModal}
        onCancel={() => setLinkModal(null)}
        footer={<Button type="primary" onClick={() => setLinkModal(null)}>Понятно</Button>}
        width={520}
      >
        {linkModal?.mailSent ? (
          <div style={{ fontSize: 12.5, color: L.text, lineHeight: 1.6 }}>
            Письмо со ссылкой ушло на <b>{linkModal.email}</b>. Сотрудник задаст пароль сам и
            войдёт по своей почте. Ссылка ниже — на случай, если письмо не дойдёт.
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: L.text, lineHeight: 1.6 }}>
            Почта пока не подключена, поэтому письмо не ушло. Передайте ссылку сотруднику лично —
            по ней он задаст пароль. Ссылка одноразовая.
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
          <Input readOnly value={linkModal?.link} className="num" style={{ fontSize: 11 }} />
          <Button
            icon={<CopyOutlined />}
            onClick={() => {
              navigator.clipboard?.writeText(linkModal?.link || '');
              message.success('Ссылка скопирована');
            }}
          >
            Копировать
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default StaffList;
